import { motion } from "framer-motion";
import { Drumstick, Sprout, HeartPulse, WheatOff, Droplets, Info } from "lucide-react";
import { EASE } from "@/lib/scroll";

const JAR_IMG = "/images/product-jar-dark.webp";

const INGREDIENTS = [
  {
    icon: Drumstick,
    title: "85% carne curată de pui",
    desc: "Proteină de înaltă biodisponibilitate, fără făinuri animale sau subproduse.",
  },
  {
    icon: Sprout,
    title: "Dovleac natural",
    desc: "Fibre solubile și beta-caroten pentru un tranzit digestiv optim.",
  },
  {
    icon: HeartPulse,
    title: "Taurină esențială",
    desc: "Pentru vedere ageră, inima puternică și imunitate de fier.",
  },
  {
    icon: WheatOff,
    title: "Fără cereale, soia sau gluten",
    desc: "Doar ingrediente naturale, potrivite carnivorului strict.",
  },
];

const PORTIONS = [
  { weight: "2–3 kg", jars: "aproximativ 1 borcan / zi" },
  { weight: "3–5 kg", jars: "aproximativ 1–2 borcane / zi" },
  { weight: "peste 5 kg", jars: "aproximativ 2 borcane / zi" },
];

export default function Recipe() {
  return (
    <section id="reteta" className="relative py-24 lg:py-36">
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-16 px-6 lg:grid-cols-12 lg:gap-12 lg:px-10">
        <motion.div
          initial={{ opacity: 0, x: -48 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 1.1, ease: EASE }}
          className="relative lg:col-span-5"
        >
          <div
            className="absolute -inset-0 translate-x-5 translate-y-5 rounded-3xl border border-gold/25"
            aria-hidden="true"
          />
          <div className="relative aspect-[4/5] overflow-hidden rounded-3xl border border-gold/40 sm:aspect-[3/3.4]">
            <img
              src={JAR_IMG}
              alt="Borcan NOSA „Pui și Dovleac” cu dovleac proaspăt și pătrunjel, pe fundal întunecat"
              className="h-full w-full object-cover"
              data-testid="recipe-jar-image"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/60 via-transparent to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 flex items-end justify-between p-6 lg:p-8">
              <span className="rounded-full border border-gold/40 bg-ink/70 px-4 py-1.5 font-mono text-[9px] uppercase tracking-[0.22em] text-gold backdrop-blur-md">
                Hrană complementară · pisici adulte
              </span>
              <span className="font-serif text-3xl text-gold-light">85 g</span>
            </div>
          </div>
        </motion.div>

        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.9, ease: EASE }}
          >
            <div className="mb-6 flex items-center gap-4">
              <span className="h-px w-12 bg-gold/70" />
              <p className="font-mono text-[10px] uppercase tracking-[0.38em] text-gold sm:text-[11px]">
                Rețeta semnătură
              </p>
            </div>
            <h2 className="font-serif text-4xl font-light leading-[1.05] tracking-tight text-cream sm:text-5xl lg:text-6xl">
              Pui și <em className="text-gold-gradient italic">Dovleac.</em>
            </h2>
            <p className="mt-6 max-w-lg text-sm leading-relaxed text-stone lg:text-base">
              O simfonie nutritivă în sticlă: carne proaspătă de pui cu dovleac, pentru o
              pisică adultă sănătoasă și hidratată.
            </p>
          </motion.div>

          <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {INGREDIENTS.map((ing, i) => (
              <motion.div
                key={ing.title}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.8, ease: EASE, delay: i * 0.08 }}
                className="group rounded-2xl border border-gold/15 bg-card p-6 transition-colors duration-500 hover:border-gold/50"
                data-testid={`recipe-ingredient-${i}`}
              >
                <ing.icon className="h-6 w-6 text-gold" strokeWidth={1.5} />
                <h3 className="mt-4 font-serif text-xl text-cream">{ing.title}</h3>
                <p className="mt-2 text-xs leading-relaxed text-stone">{ing.desc}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.1 }}
            className="mt-8 rounded-3xl border border-gold/25 bg-gradient-to-r from-card to-ink p-8 lg:p-10"
            data-testid="recipe-feeding-guide"
          >
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">
              Ghid de hrănire
            </p>
            <div className="mt-5 divide-y divide-gold/10 border-y border-gold/10">
              {PORTIONS.map((p, i) => (
                <motion.div
                  key={p.weight}
                  initial={{ opacity: 0, x: -16 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, amount: 0.15 }}
                  transition={{ duration: 0.6, ease: EASE, delay: i * 0.08 }}
                  className="flex items-center justify-between gap-6 py-4"
                  data-testid={`recipe-portion-${i}`}
                >
                  <span className="font-serif text-2xl text-gold-light">{p.weight}</span>
                  <span className="text-right font-mono text-[10px] uppercase tracking-[0.14em] text-cream sm:text-[11px]">
                    {p.jars}
                  </span>
                </motion.div>
              ))}
            </div>
            <div className="mt-6 space-y-3">
              <p
                className="flex items-start gap-3 text-sm leading-relaxed text-stone"
                data-testid="recipe-note-water"
              >
                <Droplets size={16} className="mt-0.5 shrink-0 text-gold" strokeWidth={1.5} />
                Apa proaspătă trebuie să fie disponibilă permanent.
              </p>
              <p
                className="flex items-start gap-3 text-sm leading-relaxed text-stone"
                data-testid="recipe-note-complementary"
              >
                <Info size={16} className="mt-0.5 shrink-0 text-gold" strokeWidth={1.5} />
                Deoarece NOSA este o hrană complementară, aceasta trebuie oferită alături de
                o hrană completă și echilibrată.
              </p>
              <p className="pt-1 text-xs leading-relaxed text-stone/70">
                Se servește la temperatura camerei; după deschidere, se păstrează la frigider
                și se consumă în 24–48 de ore.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
